// Chunked voxel world with an ambient-occlusion face mesher and DDA raycasting.
import * as THREE from 'three';
import { BLOCKS } from './blocks.js';
import { tileUV } from './textures.js';

export const CS = 16;
const N = BLOCKS.length;

// Fast lookup tables
const OPAQUE = new Uint8Array(256);
const OCCL = new Uint8Array(256);
const CROSS = new Uint8Array(256);
const LIQ = new Uint8Array(256);
const SOLID = new Uint8Array(256);
const GLASSLIKE = new Uint8Array(256);
const UVS = new Float32Array(256 * 3 * 4); // [top, side, bottom] x [u0,u1,v0,v1]

let tablesReady = false, uvsReady = false;
export function initVoxelTables() {
  if (tablesReady) return;
  tablesReady = true;
  for (let i = 0; i < N; i++) {
    const b = BLOCKS[i];
    OPAQUE[i] = b.opaque ? 1 : 0;
    SOLID[i] = b.solid ? 1 : 0;
    CROSS[i] = b.cross ? 1 : 0;
    LIQ[i] = b.liquid === 'water' ? 1 : b.liquid === 'lava' ? 2 : 0;
    OCCL[i] = b.solid && !b.cross && !b.liquid ? 1 : 0;
    GLASSLIKE[i] = b.name === 'glass' ? 1 : 0;
  }
}
// texture coordinates need the atlas (browser only), so they are filled lazily when meshing
function initUVs() {
  if (uvsReady) return;
  uvsReady = true;
  for (let i = 1; i < N; i++) {
    const b = BLOCKS[i];
    ['top', 'side', 'bottom'].forEach((k, j) => {
      const r = tileUV(b.tex[k]);
      UVS.set([r.u0, r.u1, r.v0, r.v1], (i * 3 + j) * 4);
    });
  }
}
export const solidId = (id) => SOLID[id] === 1;

const AO_CURVE = [0.5, 0.67, 0.84, 1.0];

// Faces: normal, 4 vertices (BL, BR, TR, TL seen from outside), texture slot (0 top,1 side,2 bottom)
const FACES = [
  { n: [1, 0, 0], v: [[1, 0, 1], [1, 0, 0], [1, 1, 0], [1, 1, 1]], t: 1 },
  { n: [-1, 0, 0], v: [[0, 0, 0], [0, 0, 1], [0, 1, 1], [0, 1, 0]], t: 1 },
  { n: [0, 1, 0], v: [[0, 1, 1], [1, 1, 1], [1, 1, 0], [0, 1, 0]], t: 0 },
  { n: [0, -1, 0], v: [[0, 0, 0], [1, 0, 0], [1, 0, 1], [0, 0, 1]], t: 2 },
  { n: [0, 0, 1], v: [[0, 0, 1], [1, 0, 1], [1, 1, 1], [0, 1, 1]], t: 1 },
  { n: [0, 0, -1], v: [[1, 0, 0], [0, 0, 0], [0, 1, 0], [1, 1, 0]], t: 1 },
];
// precompute AO sample offsets per face-vertex: [s1, s2, corner] each [dx,dy,dz]
for (const f of FACES) {
  const ax = f.n[0] !== 0 ? 0 : f.n[1] !== 0 ? 1 : 2;
  const others = [0, 1, 2].filter((a) => a !== ax);
  f.ao = f.v.map((c) => {
    const o1 = [0, 0, 0], o2 = [0, 0, 0];
    o1[others[0]] = c[others[0]] ? 1 : -1;
    o2[others[1]] = c[others[1]] ? 1 : -1;
    const s1 = [f.n[0] + o1[0], f.n[1] + o1[1], f.n[2] + o1[2]];
    const s2 = [f.n[0] + o2[0], f.n[1] + o2[1], f.n[2] + o2[2]];
    const cc = [f.n[0] + o1[0] + o2[0], f.n[1] + o1[1] + o2[1], f.n[2] + o1[2] + o2[2]];
    return [s1, s2, cc];
  });
}

class Buf {
  constructor() {
    this.pos = [];
    this.nor = [];
    this.uv = [];
    this.col = [];
    this.idx = [];
    this.n = 0;
  }
  geometry() {
    if (this.n === 0) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nor, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    g.setIndex(this.n > 65000 ? new THREE.Uint32BufferAttribute(this.idx, 1) : new THREE.Uint16BufferAttribute(this.idx, 1));
    g.computeBoundingSphere();
    g.computeBoundingBox();
    return g;
  }
}

// Mesh a padded volume (dims PX,PY,PZ incl. 1-voxel border). Positions are relative to the
// first interior voxel, then shifted by (ox,oy,oz).
export function meshVolume(vol, PX, PY, PZ, ox = 0, oy = 0, oz = 0, tint = null) {
  initVoxelTables();
  initUVs();
  const solid = new Buf(), water = new Buf(), lava = new Buf();
  const at = (x, y, z) => vol[(y * PZ + z) * PX + x];
  const ao = [0, 0, 0, 0];
  for (let y = 1; y < PY - 1; y++)
    for (let z = 1; z < PZ - 1; z++)
      for (let x = 1; x < PX - 1; x++) {
        const id = at(x, y, z);
        if (id === 0) continue;
        const bx = x - 1 + ox, by = y - 1 + oy, bz = z - 1 + oz;
        if (CROSS[id]) {
          emitCross(solid, id, bx, by, bz);
          continue;
        }
        if (BLOCKS[id].shape) {
          for (const b of BLOCKS[id].shape) for (const f of FACES) {
            const base=solid.n, ui=(id*3+f.t)*4;
            for (const v of f.v) {
              solid.pos.push(bx+b[0]+v[0]*(b[3]-b[0]),by+b[1]+v[1]*(b[4]-b[1]),bz+b[2]+v[2]*(b[5]-b[2]));
              solid.nor.push(...f.n);solid.col.push(1,1,1);
            }
            solid.uv.push(UVS[ui],UVS[ui+2],UVS[ui+1],UVS[ui+2],UVS[ui+1],UVS[ui+3],UVS[ui],UVS[ui+3]);
            solid.idx.push(base,base+1,base+2,base,base+2,base+3);solid.n+=4;
          }
          continue;
        }
        const liq = LIQ[id];
        if (liq) {
          const above = at(x, y + 1, z);
          const h = LIQ[above] === liq ? 1 : 0.875;
          const buf = liq === 1 ? water : lava;
          for (let fi = 0; fi < 6; fi++) {
            const f = FACES[fi];
            const nid = at(x + f.n[0], y + f.n[1], z + f.n[2]);
            if (LIQ[nid] === liq) continue;
            if (OPAQUE[nid]) continue;
            if (fi === 3 && nid !== 0) continue;
            emitLiquidFace(buf, f, bx, by, bz, h);
          }
          continue;
        }
        const uvBase = id * 3;
        for (let fi = 0; fi < 6; fi++) {
          const f = FACES[fi];
          const nid = at(x + f.n[0], y + f.n[1], z + f.n[2]);
          if (OPAQUE[nid]) continue;
          if (nid === id && GLASSLIKE[id]) continue;
          // AO
          for (let k = 0; k < 4; k++) {
            const [s1, s2, cc] = f.ao[k];
            const a = OCCL[at(x + s1[0], y + s1[1], z + s1[2])];
            const b = OCCL[at(x + s2[0], y + s2[1], z + s2[2])];
            const c = OCCL[at(x + cc[0], y + cc[1], z + cc[2])];
            ao[k] = a && b ? 0 : 3 - (a + b + c);
          }
          const ui = (uvBase + f.t) * 4;
          const u0 = UVS[ui], u1 = UVS[ui + 1], v0 = UVS[ui + 2], v1 = UVS[ui + 3];
          const base = solid.n;
          const tr = tint ? tint(bx, by, bz, id) : 1;
          for (let k = 0; k < 4; k++) {
            const c = f.v[k];
            solid.pos.push(bx + c[0], by + c[1], bz + c[2]);
            solid.nor.push(f.n[0], f.n[1], f.n[2]);
            const l = AO_CURVE[ao[k]] * tr;
            solid.col.push(l, l, l);
          }
          solid.uv.push(u0, v0, u1, v0, u1, v1, u0, v1);
          if (ao[0] + ao[2] > ao[1] + ao[3]) solid.idx.push(base + 1, base + 2, base + 3, base + 1, base + 3, base);
          else solid.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
          solid.n += 4;
        }
      }
  return { solid: solid.geometry(), water: water.geometry(), lava: lava.geometry() };
}

function emitCross(buf, id, x, y, z) {
  const ui = (id * 3 + 1) * 4;
  const u0 = UVS[ui], u1 = UVS[ui + 1], v0 = UVS[ui + 2], v1 = UVS[ui + 3];
  const a = 0.15, b = 0.85;
  const quads = [
    [[a, 0, a], [b, 0, b], [b, 1, b], [a, 1, a]],
    [[a, 0, b], [b, 0, a], [b, 1, a], [a, 1, b]],
  ];
  for (const q of quads) {
    for (let side = 0; side < 2; side++) {
      const base = buf.n;
      for (const c of q) {
        buf.pos.push(x + c[0], y + c[1], z + c[2]);
        buf.nor.push(0, 1, 0);
        buf.col.push(1, 1, 1);
      }
      buf.uv.push(u0, v0, u1, v0, u1, v1, u0, v1);
      if (side === 0) buf.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
      else buf.idx.push(base, base + 2, base + 1, base, base + 3, base + 2);
      buf.n += 4;
    }
  }
}

function emitLiquidFace(buf, f, x, y, z, h) {
  const base = buf.n;
  const S = 0.5; // texture repeats every 2 blocks
  for (let k = 0; k < 4; k++) {
    const c = f.v[k];
    const py = c[1] === 1 ? h : 0;
    const wx = x + c[0], wy = y + py, wz = z + c[2];
    buf.pos.push(wx, wy, wz);
    buf.nor.push(f.n[0], f.n[1], f.n[2]);
    buf.col.push(1, 1, 1);
    if (f.n[1] !== 0) buf.uv.push(wx * S, wz * S);
    else if (f.n[0] !== 0) buf.uv.push(wz * S, wy * S);
    else buf.uv.push(wx * S, wy * S);
  }
  buf.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  buf.n += 4;
}

// String keys avoid the old packed-key collisions beyond 8,192 blocks.
const ckey = (cx, cy, cz) => `${cx},${cy},${cz}`;

export class VoxelWorld {
  constructor(materials) {
    initVoxelTables();
    this.chunks = new Map();
    this.meshes = new Map();
    this.dirty = new Set();
    this.group = new THREE.Group();
    this.mats = materials;
    this._lk = -1;
    this._lc = null;
    this.min = new THREE.Vector3(Infinity, Infinity, Infinity);
    this.max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
    this.tint = null;
  }

  chunk(cx, cy, cz, create) {
    const k = ckey(cx, cy, cz);
    if (k === this._lk) return this._lc;
    let c = this.chunks.get(k);
    if (!c && create) {
      c = new Uint8Array(CS * CS * CS);
      this.chunks.set(k, c);
    }
    if (c) {
      this._lk = k;
      this._lc = c;
    }
    return c;
  }

  get(x, y, z) {
    const c = this.chunk(x >> 4, y >> 4, z >> 4, false);
    return c ? c[((y & 15) << 8) | ((z & 15) << 4) | (x & 15)] : 0;
  }

  getF(x, y, z) {
    return this.get(Math.floor(x), Math.floor(y), Math.floor(z));
  }

  solid(x, y, z) {
    return SOLID[this.get(x, y, z)] === 1;
  }

  solidF(x, y, z) {
    return SOLID[this.get(Math.floor(x), Math.floor(y), Math.floor(z))] === 1;
  }

  set(x, y, z, id, live = false) {
    x |= 0;
    y |= 0;
    z |= 0;
    const c = this.chunk(x >> 4, y >> 4, z >> 4, true);
    c[((y & 15) << 8) | ((z & 15) << 4) | (x & 15)] = id;
    if (id) {
      if (x < this.min.x) this.min.x = x;
      if (y < this.min.y) this.min.y = y;
      if (z < this.min.z) this.min.z = z;
      if (x > this.max.x) this.max.x = x;
      if (y > this.max.y) this.max.y = y;
      if (z > this.max.z) this.max.z = z;
    }
    if (live) {
      for (let dy = -1; dy <= 1; dy++)
        for (let dz = -1; dz <= 1; dz++)
          for (let dx = -1; dx <= 1; dx++) this.dirty.add(ckey((x + dx) >> 4, (y + dy) >> 4, (z + dz) >> 4));
    } else this.dirty.add(ckey(x >> 4, y >> 4, z >> 4));
  }

  // set only if the cell is empty
  place(x, y, z, id) {
    if (this.get(x, y, z) === 0) this.set(x, y, z, id);
  }

  fill(x0, y0, z0, x1, y1, z1, id) {
    for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y++)
      for (let z = Math.min(z0, z1); z <= Math.max(z0, z1); z++)
        for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x++) this.set(x, y, z, id);
  }

  dirtyRegion(x0, z0, size) {
    for (const k of this.chunks.keys()) {
      const [cx,,cz] = k.split(',').map(Number);
      if (cx * CS >= x0 - CS && cx * CS <= x0 + size && cz * CS >= z0 - CS && cz * CS <= z0 + size) this.dirty.add(k);
    }
  }

  unloadRegion(x0, z0, size) {
    for (const k of this.chunks.keys()) {
      const [cx,,cz] = k.split(',').map(Number);
      if (cx * CS < x0 || cx * CS >= x0 + size || cz * CS < z0 || cz * CS >= z0 + size) continue;
      for (const m of this.meshes.get(k) || []) { this.group.remove(m); m.geometry.dispose(); }
      this.meshes.delete(k); this.chunks.delete(k); this.dirty.delete(k);
    }
    this._lk = -1; this._lc = null;
    this.dirtyRegion(x0, z0, size);
  }

  // Rebuild meshes for dirty chunks
  rebuild() {
    if (this.dirty.size === 0) return;
    const PX = CS + 2;
    const vol = new Uint8Array(PX * PX * PX);
    for (const k of this.dirty) {
      const [cx, cy, cz] = k.split(',').map(Number);
      const old = this.meshes.get(k);
      if (old) {
        for (const m of old) {
          this.group.remove(m);
          m.geometry.dispose();
        }
        this.meshes.delete(k);
      }
      if (!this.chunks.has(k)) continue;
      const x0 = cx * CS - 1, y0 = cy * CS - 1, z0 = cz * CS - 1;
      let any = false;
      for (let y = 0; y < PX; y++)
        for (let z = 0; z < PX; z++)
          for (let x = 0; x < PX; x++) {
            const v = this.get(x0 + x, y0 + y, z0 + z);
            vol[(y * PX + z) * PX + x] = v;
            if (v) any = true;
          }
      if (!any) continue;
      const res = meshVolume(vol, PX, PX, PX, 0, 0, 0, this.tint ? (x, y, z, id) => this.tint(x + cx * CS, y + cy * CS, z + cz * CS, id) : null);
      const list = [];
      const add = (geo, mat, cast, recv) => {
        if (!geo) return;
        const m = new THREE.Mesh(geo, mat);
        m.position.set(cx * CS, cy * CS, cz * CS);
        m.castShadow = cast;
        m.receiveShadow = recv;
        m.matrixAutoUpdate = false;
        m.updateMatrix();
        this.group.add(m);
        list.push(m);
      };
      add(res.solid, this.mats.solid, true, true);
      add(res.water, this.mats.water, false, true);
      add(res.lava, this.mats.lava, false, false);
      this.meshes.set(k, list);
    }
    this.dirty.clear();
  }

  dispose() {
    for (const list of this.meshes.values())
      for (const m of list) {
        this.group.remove(m);
        m.geometry.dispose();
      }
    this.meshes.clear();
    this.chunks.clear();
    this._lk = -1;
    this._lc = null;
  }

  // Amanatides & Woo voxel traversal. Returns {x,y,z,nx,ny,nz,dist,id} or null
  raycast(ox, oy, oz, dx, dy, dz, maxDist, test = (id) => SOLID[id] === 1 && OPAQUE[id] === 1) {
    const len = Math.hypot(dx, dy, dz) || 1;
    dx /= len;
    dy /= len;
    dz /= len;
    let x = Math.floor(ox), y = Math.floor(oy), z = Math.floor(oz);
    const sx = dx > 0 ? 1 : -1, sy = dy > 0 ? 1 : -1, sz = dz > 0 ? 1 : -1;
    const tdx = Math.abs(1 / dx), tdy = Math.abs(1 / dy), tdz = Math.abs(1 / dz);
    let tmx = dx !== 0 ? (dx > 0 ? x + 1 - ox : ox - x) * tdx : Infinity;
    let tmy = dy !== 0 ? (dy > 0 ? y + 1 - oy : oy - y) * tdy : Infinity;
    let tmz = dz !== 0 ? (dz > 0 ? z + 1 - oz : oz - z) * tdz : Infinity;
    let nx = 0, ny = 0, nz = 0, t = 0;
    for (let i = 0; i < 256; i++) {
      const id = this.get(x, y, z);
      if (id && test(id)) return { x, y, z, nx, ny, nz, dist: t, id };
      if (tmx < tmy && tmx < tmz) {
        x += sx;
        t = tmx;
        tmx += tdx;
        nx = -sx;
        ny = 0;
        nz = 0;
      } else if (tmy < tmz) {
        y += sy;
        t = tmy;
        tmy += tdy;
        nx = 0;
        ny = -sy;
        nz = 0;
      } else {
        z += sz;
        t = tmz;
        tmz += tdz;
        nx = 0;
        ny = 0;
        nz = -sz;
      }
      if (t > maxDist) return null;
    }
    return null;
  }
}

// ---- materials ----
export function createVoxelMaterials({ atlas, glow }, waterTex, lavaTex) {
  const solid = new THREE.MeshLambertMaterial({
    map: atlas,
    emissiveMap: glow,
    emissive: 0xffffff,
    emissiveIntensity: 1.15,
    vertexColors: true,
    alphaTest: 0.5,
  });
  const water = new THREE.MeshLambertMaterial({
    map: waterTex,
    transparent: true,
    opacity: 0.78,
    emissive: 0x16345c,
    emissiveIntensity: 0.6,
    depthWrite: false,
  });
  const lava = new THREE.MeshBasicMaterial({ map: lavaTex, color: new THREE.Color(1.7, 1.25, 1.0) });
  return { solid, water, lava };
}

// ---- single block / small structure geometry ----
const blockGeoCache = new Map();
export function blockGeometry(id) {
  if (blockGeoCache.has(id)) return blockGeoCache.get(id);
  const vol = new Uint8Array(27);
  vol[13] = id;
  const res = meshVolume(vol, 3, 3, 3, -0.5, -0.5, -0.5);
  const g = res.solid;
  blockGeoCache.set(id, g);
  return g;
}

// Build geometry from a small block list [{x,y,z,id}] (positions relative to an origin)
export function meshBlockList(list) {
  if (!list.length) return { solid: null, water: null, lava: null };
  let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (const b of list) {
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    minZ = Math.min(minZ, b.z);
    maxX = Math.max(maxX, b.x);
    maxY = Math.max(maxY, b.y);
    maxZ = Math.max(maxZ, b.z);
  }
  const PX = maxX - minX + 3, PY = maxY - minY + 3, PZ = maxZ - minZ + 3;
  const vol = new Uint8Array(PX * PY * PZ);
  for (const b of list) vol[((b.y - minY + 1) * PZ + (b.z - minZ + 1)) * PX + (b.x - minX + 1)] = b.id;
  return meshVolume(vol, PX, PY, PZ, minX, minY, minZ);
}
